import { getLang } from '../i18n';

/** Zahlen- und Datumsformat folgt der gewählten Sprache (Deutsch: 1.234,50 €, Englisch: €1,234.50). */
const loc = () => (getLang() === 'en' ? 'en-GB' : 'de-DE');
const cache = new Map<string, Intl.NumberFormat>();
function nf(digits: 0 | 2): Intl.NumberFormat {
  const k = `${loc()}|${digits}`;
  let f = cache.get(k);
  if (!f) { f = new Intl.NumberFormat(loc(), { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits }); cache.set(k, f); }
  return f;
}

export const eur = (cents: number) => nf(0).format(Math.round(cents / 100));
export const eur2 = (cents: number) => nf(2).format(cents / 100);
/** Tausend Euro mit einer Nachkommastelle, ohne Währungszeichen (z. B. 119,4) – für kompakte Monatsspalten */
export const tk = (cents: number) => (cents / 100_000).toLocaleString(loc(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const pct = (v: number) => (getLang() === 'en' ? `${Math.round(v * 100)}%` : `${Math.round(v * 100)} %`);

/** 'YYYY-MM-DD' → '05.10.2026' bzw. '05/10/2026' */
export function dfmt(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return getLang() === 'en' ? `${d}/${m}/${y}` : `${d}.${m}.${y}`;
}

export function dtfmt(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(loc(), { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString(loc(), { hour: '2-digit', minute: '2-digit' })}`;
}

/** Cent → Eingabefeld-Text in der gewählten Sprache (120.000,50 bzw. 120,000.50). */
export function centsToField(cents: number): string {
  return new Intl.NumberFormat(loc(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
}

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CCOL: Record<string, string> = { BMW: 'var(--c1)', VW: 'var(--c3)', Mersdirect: 'var(--c2)', Swipecor: 'var(--c4)', OACE: 'var(--c5)' };
export const custColor = (name: string) => CCOL[name] ?? 'var(--faint)';

/** Kompakt für Kacheln und Sätze: ab 1 Mio „4,83 Mio €“, sonst „508 T€“, unter 1.000 € ganze Euro. */
export function keur(cents: number): string {
  const e = cents / 100;
  const a = Math.abs(e);
  const en = getLang() === 'en';
  if (a >= 1_000_000) return en ? `€${(e / 1e6).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}m` : `${(e / 1e6).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mio €`;
  if (a >= 1_000) return en ? `€${Math.round(e / 1000).toLocaleString('en-GB')}k` : `${Math.round(e / 1000).toLocaleString('de-DE')} T€`;
  return eur(cents);
}

/** Vorzeichen immer zeigen: „+11 T€“, „−87 T€“ */
export const skeur = (cents: number) => (cents < 0 ? `−${keur(-cents)}` : `+${keur(cents)}`);

/** Anzeigename eines Projekts in Listen: Kurzname, sonst Name. */
export const pname = (p: { name: string; shortName?: string | null }) => p.shortName || p.name;
